import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import useStore from '../store'
import GoalCard from '../components/goals/GoalCard'
import GoalForm from '../components/goals/GoalForm'
import SurvivalFilter from '../components/goals/SurvivalFilter'

const TABS = [
  {
    id:    'active',
    label: 'Active',
    desc:  'Goals you are currently working on. Hard cap enforced.',
  },
  {
    id:    'backlog',
    label: 'Backlog',
    desc:  'Goals you\'re considering but not yet committed to.',
  },
  {
    id:    'completed',
    label: 'Completed',
    desc:  'Goals you\'ve finished.',
  },
]

export default function GoalsPage() {
  const goals       = useStore(s => s.goals)
  const profile     = useStore(s => s.profile)
  const constraints = useStore(s => s.constraints)

  const [tab,        setTab]       = useState('active')
  const [showForm,   setShowForm]  = useState(false)
  const [formBucket, setFormBucket] = useState('aspirational')

  const cap = profile?.active_goal_cap ?? 3

  const active    = useMemo(() => goals.filter(g => ['active','paused','committed'].includes(g.status)), [goals])
  const backlog   = useMemo(() => goals.filter(g => g.bucket === 'aspirational' && !['completed','archived'].includes(g.status)), [goals])
  const completed = useMemo(() => goals.filter(g => g.status === 'completed'), [goals])

  const byTab  = { active, backlog, completed }
  const counts = { active: active.length, backlog: backlog.length, completed: completed.length }

  const currentTabDesc = TABS.find(t => t.id === tab)?.desc

  const openForm = (bucket) => { setFormBucket(bucket); setShowForm(true) }

  return (
    <motion.div
      className="h-full flex flex-col"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
    >
      {/* ── Header ── */}
      <div className="shrink-0 border-b border-m-border px-5 py-3">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-mono text-sm text-m-text font-medium">Goals</h1>
            <div className="flex items-center gap-3 mt-1">
              {/* Active cap indicator */}
              <div className="flex items-center gap-1">
                {Array.from({ length: cap }).map((_, i) => (
                  <div key={i} className={`w-4 h-1.5 ${i < active.length ? 'bg-m-accent' : 'bg-m-muted'}`} />
                ))}
              </div>
              <span className="font-mono text-[10px] text-m-subtle">
                {active.length}/{cap} active slots
                {constraints?.atCap && <span className="text-m-warn ml-1">· cap reached</span>}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => openForm('aspirational')} className="btn-ghost btn-sm">
              + Backlog
            </button>
            <button
              onClick={() => openForm('committed')}
              disabled={constraints?.atCap}
              title={constraints?.atCap ? `Active goal cap (${cap}) reached. Complete or pause a goal first.` : 'Add a committed goal'}
              className={`btn-sm ${
                constraints?.atCap
                  ? 'btn-ghost opacity-40 cursor-not-allowed'
                  : 'btn-accent-ghost'
              }`}
            >
              {constraints?.atCap ? `Cap: ${cap}/${cap}` : '+ Commit goal'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Survival filter (unlocked feature) ── */}
      <SurvivalFilter />

      {/* ── Tabs ── */}
      <div className="shrink-0 border-b border-m-border flex px-5">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`font-mono text-[10px] tracking-widest uppercase px-4 py-2.5 border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-m-accent text-m-text'
                : 'border-transparent text-m-faint hover:text-m-subtle'
            }`}>
            {t.label}
            {counts[t.id] > 0 && (
              <span className="ml-1.5 text-m-faint">{counts[t.id]}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab description ── */}
      <div className="shrink-0 px-5 py-2 border-b border-m-border bg-m-surface/50">
        <p className="font-mono text-[10px] text-m-faint">{currentTabDesc}</p>
      </div>

      {/* ── Goal list ── */}
      <div className="flex-1 overflow-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="px-5 py-4 max-w-2xl mx-auto"
          >
            {byTab[tab].length === 0 ? (
              <EmptyState tab={tab} cap={cap} onAdd={openForm} atCap={constraints?.atCap} />
            ) : (
              <div className="flex flex-col gap-2.5">
                <AnimatePresence>
                  {byTab[tab].map(g => <GoalCard key={g.id} goal={g} />)}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Create goal panel ── */}
      <AnimatePresence>
        {showForm && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/60 z-40"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowForm(false)}
            />
            <motion.div
              className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-m-surface border-l border-m-border z-50 flex flex-col"
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            >
              <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-m-border">
                <div>
                  <span className="font-mono text-xs tracking-widest uppercase text-m-text">
                    {formBucket === 'committed' ? 'New committed goal' : 'New backlog goal'}
                  </span>
                  <p className="font-mono text-[10px] text-m-faint mt-0.5">
                    {formBucket === 'committed'
                      ? 'Allocated to your timeline immediately.'
                      : 'Saved for consideration. No time allocated yet.'}
                  </p>
                </div>
                <button onClick={() => setShowForm(false)}
                  className="font-mono text-m-faint hover:text-m-text text-xl leading-none w-8 h-8 flex items-center justify-center">
                  ×
                </button>
              </div>
              <div className="flex-1 overflow-auto p-5">
                <GoalForm defaultBucket={formBucket} onDone={() => setShowForm(false)} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// ── Empty state per tab ────────────────────────────────────────────────────────
function EmptyState({ tab, cap, onAdd, atCap }) {
  const content = {
    active: {
      title: 'No active goals',
      body:  atCap
        ? `Your active cap of ${cap} is full. Complete or pause a goal before adding another.`
        : 'Committed goals appear here. They are allocated to your life timeline and count against your weekly hours.',
      cta:   atCap ? null : 'Commit your first goal →',
      action: () => onAdd('committed'),
    },
    backlog: {
      title: 'Backlog is empty',
      body:  'Backlog goals are things you want to do eventually. No time is allocated — they just sit here until you\'re ready to commit.',
      cta:   'Add a backlog goal →',
      action: () => onAdd('aspirational'),
    },
    completed: {
      title: 'No completed goals yet',
      body:  'Goals you mark complete appear here. Your history of finished work.',
      cta:   null,
      action: null,
    },
  }

  const { title, body, cta, action } = content[tab]

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="py-16 flex flex-col items-center text-center gap-4"
    >
      <p className="font-mono text-base text-m-text">{title}</p>
      <p className="font-mono text-xs text-m-subtle leading-relaxed max-w-xs">{body}</p>
      {cta && action && (
        <button onClick={action} className="btn-accent-ghost btn mt-2">
          {cta}
        </button>
      )}
    </motion.div>
  )
}
